// Collection page (sections/collection.liquid): filters, sort and "load more" without page reloads.
// Every control is a link or GET form; here they fetch the same URL with section_id and swap [data-results] and
// the filter panel's form. The blob field follows the moving cards by itself (MutationObserver in blob-field.js).

const root = document.querySelector('[data-collection]')
if (root) {
  const reduced = document.documentElement.classList.contains('reduced')
  const section = root.dataset.section
  const dlg = root.querySelector('#Facets')
  const results = () => root.querySelector('[data-results]')
  let ctrl = null

  const fetchSection = async (url) => {
    ctrl?.abort()
    ctrl = new AbortController()
    const u = new URL(url, location.href)
    u.searchParams.set('section_id', section)
    const r = await fetch(u, { signal: ctrl.signal })
    return new DOMParser().parseFromString(await r.text(), 'text/html')
  }

  // ---------- buying guide: lead, brand tiles, steps, folded with "Weiterlesen" ----------
  const guide = root.querySelector('[data-guide-body]')
  if (guide) {
    const body = guide.querySelector('.metafield-rich_text_field') || guide
    body.querySelector(':scope > p')?.classList.add('g-lead')
    // "Brand (OS):" paragraph + the list under it = one tile; tiles that follow each other share a grid
    for (const p of [...body.querySelectorAll(':scope > p')]) {
      const list = p.nextElementSibling
      if (!list?.matches('ul') || !/:\s*$/.test(p.textContent)) continue // numbered lists become steps, below
      const colon = [...p.querySelectorAll('*'), p].flatMap((n) => [...n.childNodes]).filter((n) => n.nodeType === 3 && /:\s*$/.test(n.textContent)).pop()
      if (colon) colon.textContent = colon.textContent.replace(/\s*:\s*$/, '')
      let grid = p.previousElementSibling
      if (!grid?.classList.contains('g-tiles')) {
        grid = document.createElement('div')
        grid.className = 'g-tiles'
        p.before(grid)
      }
      const tile = document.createElement('div')
      tile.className = 'g-tile'
      tile.append(p, list)
      grid.append(tile)
      // "🏆 <strong>Der Kamera-Geheimtipp</strong>": an emoji before the bold text = a small tag, not a heading
      for (const li of list.children) if (li.firstChild?.nodeType === 3 && li.firstChild.textContent.trim() && li.firstElementChild?.tagName === 'STRONG') li.classList.add('g-tag')
    }
    // as many columns as fill even rows: 5 → 3 + 2, 6 → 3 + 3, 7–8 → 4
    body.querySelectorAll('.g-tiles').forEach((g) => {
      const n = g.children.length
      g.style.setProperty('--cols', n <= 4 ? n : Math.min(4, Math.ceil(n / 2)))
    })
    body.querySelectorAll(':scope > ol').forEach((ol) => ol.classList.add('g-steps'))
    const more = root.querySelector('[data-guide-more]')
    if (guide.scrollHeight > 760) {
      guide.classList.add('is-folded')
      more.hidden = false
      more.addEventListener('click', () => {
        const open = guide.classList.toggle('is-folded') === false
        more.setAttribute('aria-expanded', String(open))
        more.textContent = open ? more.dataset.less : more.dataset.more
        if (!open) guide.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' })
      })
    }
  }

  // ---------- product count without the hidden twins ----------
  // The grid leaves out a "-differenzbesteuert" card while its regular product is listed (it covers both); Shopify's
  // count still has both. One light request for all handles (templates/collection.handles.liquid, same filters).
  // the bar's count and the panel's "N Produkte anzeigen" (url: the list just rendered, before Back/push settle)
  const fixCount = async (url = location.href) => {
    const u = new URL(url, location.href)
    u.searchParams.set('view', 'handles')
    u.searchParams.delete('page')
    u.searchParams.delete('section_id')
    const list = await fetch(u).then((r) => r.text()).catch(() => '')
    const handles = new Set(list.split(',').map((h) => h.trim()).filter(Boolean))
    if (!handles.size) return
    // same rule as the grid: only without filters other than "in stock"
    if ([...u.searchParams.keys()].some((k) => k.startsWith('filter.') && !k.includes('availability'))) return
    let hidden = 0
    // like the Liquid side ("| remove"): also "…-differenzbesteuert-1" pairs with "…-1"
    for (const h of handles) if (h.includes('-differenzbesteuert') && handles.has(h.replace('-differenzbesteuert', ''))) hidden++
    // looked up now, not before the fetch: the results and the panel get swapped in meanwhile
    const els = [root.querySelector('[data-count]'), root.querySelector('#Facets button[type=submit]')].filter(Boolean)
    if (hidden) for (const el of els) el.textContent = el.textContent.replace(/\d[\d.]*/, (n) => (parseInt(n.replace(/\./g, ''), 10) - hidden).toLocaleString('de-DE'))
  }
  fixCount()

  // Price sorts: a card shows the cheapest of the device and its "-differenzbesteuert" twin, but Shopify and the
  // Liquid re-sort (which can't look up 250 twins, 20 lookups per page) only know the device's own price. So the cards
  // are put in order by the price they show; other tiles keep their places.
  const sortShown = (url) => {
    const dir = /sort_by=price-(a|de)scending/.exec(url)?.[1]
    const grid = root.querySelector('[data-grid]')
    if (!dir || !grid) return
    const price = (li) => Number(li.querySelector('.card__price').textContent.replace(/[^\d,]/g, '').replace(',', '.'))
    const items = [...grid.children]
    const cards = items.filter((li) => li.querySelector('.card__price'))
    const sorted = [...cards].sort((a, b) => (dir === 'a' ? price(a) - price(b) : price(b) - price(a)))
    if (sorted.every((li, i) => li === cards[i])) return
    let k = 0
    grid.append(...items.map((li) => (li.querySelector('.card__price') ? sorted[k++] : li)))
  }
  sortShown(location.href)

  /** Load a filtered/sorted URL and swap results and filter panel. */
  async function go(url, push = true) {
    results().setAttribute('aria-busy', 'true')
    try {
      const doc = await fetchSection(url)
      const fresh = doc.querySelector('[data-results]')
      fresh.classList.add('is-swapped') // only the cards play their entrance again, not the filter rows
      results().replaceWith(fresh)
      sortShown(new URL(url, location.href).href)
      fixCount(url)
      // the panel may be open while it re-renders: keep open groups, scroll position and focus
      const old = root.querySelector('[data-facets]')
      const opened = [...old.querySelectorAll('details')].map((d) => d.open)
      const top = old.querySelector('.facets__body').scrollTop
      const a = document.activeElement
      const focus = old.contains(a) && a.name ? `[name="${CSS.escape(a.name)}"][value="${CSS.escape(a.value)}"]` : null
      const facets = doc.querySelector('[data-facets]')
      facets.querySelectorAll('details').forEach((d, i) => (d.open = opened[i] ?? d.open))
      old.replaceWith(facets)
      facets.querySelector('.facets__body').scrollTop = top
      if (focus) facets.querySelector(focus)?.focus()
      if (push && new URL(url, location.href).href !== location.href) history.pushState(null, '', url) // no duplicate entries for Back
      shown = new URL(url, location.href).href
      autoMore()
    } catch (e) {
      if (e.name !== 'AbortError') location.assign(url) // anything odd: fall back to a normal page load
    }
  }

  /** Form → URL. Empty fields (price boxes) are left out so they don't count as active filters. */
  const formUrl = (form) => {
    const p = new URLSearchParams()
    for (const [k, v] of new FormData(form)) if (v !== '') p.append(k, v)
    return `${form.action}?${p}`
  }

  /** Append the next page instead of replacing. */
  async function more(link, fallback = true) {
    link.setAttribute('aria-busy', 'true')
    try {
      const doc = await fetchSection(link.href)
      if (!link.isConnected) return // the list was re-rendered meanwhile (filter, sort, Back): this page belongs to the old one
      root.querySelector('[data-grid]').append(...doc.querySelectorAll('[data-grid] > li'))
      const next = doc.querySelector('[data-more]')
      next ? root.querySelector('[data-more]').replaceWith(next) : root.querySelector('[data-more]').remove()
    } catch (err) {
      if (err.name !== 'AbortError' && fallback) location.assign(link.href)
    }
  }

  // The first page is small (each listed product adds ~10 KB of Shopify analytics to <head>), so the page paints
  // fast; once it's idle, the next page is appended by itself. After that it's the "load more" button.
  const autoMore = () => {
    const link = root.querySelector('[data-more-link]')
    if (link) ('requestIdleCallback' in window ? requestIdleCallback : setTimeout)(() => link.isConnected && more(link, false))
  }

  root.addEventListener('click', (e) => {
    const link = e.target.closest('[data-more-link]')
    if (link) {
      e.preventDefault()
      return more(link)
    }
    const filter = e.target.closest('a[data-filter-link]')
    if (filter) {
      e.preventDefault() // also keeps the page transition (motion.js) out of filtering
      go(filter.href)
      if (dlg.open && !filter.closest('[data-facets]')) close()
      return
    }
    const opener = e.target.closest('[data-facets-open]')
    if (opener) {
      open()
      // "+N weitere": straight to the series, opened and in view
      const g = opener.dataset.facetsGroup && dlg.querySelector(`[data-group*="${opener.dataset.facetsGroup}"]`)
      if (g) (g.open = true), g.scrollIntoView({ block: 'start' })
    }
    else if (e.target === dlg || (e.target.closest('[data-facets-close]') && e.target.type !== 'submit')) close()
  })

  root.addEventListener('change', (e) => {
    const form = e.target.closest('form')
    if (form?.matches('[data-facets]')) go(formUrl(form)) // live (filters and sort): counts update while the panel is open
  })

  root.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets]')
    if (!form) return
    e.preventDefault()
    go(formUrl(form))
    close()
  })

  // the rating card's customer quotes: the next one every 6s (the grid may have been swapped by a filter)
  if (!reduced)
    setInterval(() => {
      const qs = [...root.querySelectorAll('[data-quotes] > *')]
      const i = qs.findIndex((q) => q.classList.contains('is-on'))
      if (qs.length < 2) return
      qs[i]?.classList.remove('is-on')
      qs[(i + 1) % qs.length].classList.add('is-on')
    }, 6000)

  function open() {
    dlg.showModal()
    window.janado?.lenis?.stop()
    if (!reduced) dlg.animate([{ transform: 'translateX(100%)' }, { transform: 'none' }], { duration: 700, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' })
  }
  function close() {
    if (!dlg.open) return
    if (reduced) return dlg.close()
    dlg.animate([{ transform: 'none' }, { transform: 'translateX(100%)' }], { duration: 400, easing: 'cubic-bezier(0.5, 0, 0.75, 0)' }).onfinish = () => dlg.close()
  }
  dlg.addEventListener('cancel', (e) => {
    e.preventDefault()
    close()
  })
  dlg.addEventListener('close', () => window.janado?.lenis?.start())

  // only when Back/Forward really changes the list: pop-ups (Ankauf-Tool, finder) push entries with the same URL, and
  // closing them must not reload the grid (and drop the pages loaded with "Mehr laden")
  let shown = location.href
  addEventListener('popstate', () => location.href !== shown && go(location.href, false))
  if (document.readyState === 'complete') autoMore()
  else addEventListener('load', autoMore, { once: true })
}
